import { HashRouter, Routes, Route, useLocation, useNavigationType, useNavigate } from "react-router-dom"
import { lazy, Suspense, createContext, useContext, useRef, useMemo, useEffect, useCallback, memo, useState } from "react"
import { useAuthStore } from "@/lib/store/useAuthStore"
import { useShareTarget } from "@/hooks/useShareTarget"
import { usePushNotifications } from "@/hooks/usePushNotifications"
import { usePurchases } from "@/lib/purchases/usePurchases"
import { useGlobalPicksSync } from '@/lib/api/hooks/useGlobalPicksSync'
import { usePickIntentStore } from "@/lib/store/usePickIntentStore"
import { useUIStore } from "@/lib/store/useUIStore"
import { ReloadPrompt } from "./components/ReloadPrompt"
import { Toaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from '@/lib/query-client'
import { ErrorBoundary } from "./components/ErrorBoundary"
import { Capacitor } from "@capacitor/core"
import { App as CapacitorApp } from "@capacitor/app"
import { StatusBar } from "@capacitor/status-bar"
import { toast } from "sonner"
import { WifiOff } from "lucide-react"

// Lazy load route components
const Layout = lazy(() => import("./Layout"))
const Home = lazy(() => import("./pages/Home"))
const Details = lazy(() => import("./pages/Details"))
const PickDetail = lazy(() => import("@/pages/PickDetail"))
const Collections = lazy(() => import("@/pages/Collections"))
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"))
const FolderDetail = lazy(() => import("./pages/FolderDetail"))
const Unsorted = lazy(() => import("./pages/Unsorted"))
const Favourites = lazy(() => import("./pages/Favourites"))
const Profile = lazy(() => import("./pages/Profile"))
const Search = lazy(() => import("./pages/Search"))
const Settings = lazy(() => import("./pages/Settings"))
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"))

// Auth Routes
const AuthMethod = lazy(() => import("@/pages/auth/AuthMethod"))
const EmailAuth = lazy(() => import("@/pages/auth/EmailAuth"))
const VerifyOtp = lazy(() => import("@/pages/auth/VerifyOtp"))
const InviteAccept = lazy(() => import("@/pages/InviteAccept"))
const CollectionPreview = lazy(() => import("@/pages/CollectionPreview"))
const AuthCallback = lazy(() => import("@/pages/auth/AuthCallback"))
const ProtectedRoute = lazy(() => import("@/components/auth/ProtectedRoute"))
const ProUpgrade = lazy(() => import("@/pages/ProUpgrade"))
import { OnboardingGate } from "@/components/auth/OnboardingGate"
import { ForceUpdateGate } from "@/components/auth/ForceUpdateGate"
import { trackEvent } from "@/lib/analytics"

// Web-only components
const LandingPage = lazy(() => import("./pages/web/LandingPage"))
const PublicCollectionPreview = lazy(() => import("./pages/web/PublicCollectionPreview"))
const LegalSettings = lazy(() => import("@/pages/LegalSettings"))
const LegalPage = lazy(() => import("./pages/web/LegalPage"))
const DebugUI = lazy(() => import("./pages/DebugUI"))
const Onboarding = lazy(() => import("@/pages/Onboarding"))

const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"))

// Navigation Context
const NavigationContext = createContext<{ action: string }>({ action: 'PUSH' })
export const useNavigationAction = () => useContext(NavigationContext)

/**
 * AppManagers handles all background side-effects (Auth, Deep Links, Share Target)
 * without re-rendering the main UI components unnecessarily.
 */
let globalHasBooted = false;
let globalProcessedLaunchUrl = false;

const AppManagers = memo(() => {
  const { initialize, listenForDeepLink } = useAuthStore()
  
  // Initialize Background Managers
  usePushNotifications()
  usePurchases()
  
  const navigate = useNavigate()
  
  // 1. Initialize Listeners
  useShareTarget()
  useGlobalPicksSync()

  const handleDeepLink = useCallback((url: string) => {
    try {
      let normalizedUrl = url.startsWith('shuvo://') ? url.replace('shuvo://', 'https://shuvo.pro/') : url
      const parsedUrl = new URL(normalizedUrl)
      let path = parsedUrl.hash ? parsedUrl.hash.replace('#', '') : (parsedUrl.pathname + parsedUrl.search)
      
      if (path.startsWith('/auth/')) {
        navigate(path, { replace: true })
      } else if (path.startsWith('/s/') || path.startsWith('/invite/') || path.startsWith('/pick/')) {
        const parts = path.split('/')
        const isShare = path.startsWith('/s/')
        const token = parts[2]?.split('?')[0]
        if (token) navigate(`/preview/${token}${isShare ? '?mode=save' : ''}`, { replace: true })
      } else if (path === '/create-collection' || path === 'create-collection') {
        useUIStore.getState().setIsAddCollectionOpen(true)
      } else if (path.startsWith('/upgrade') || path.startsWith('upgrade')) {
        navigate('/upgrade')
      }
    } catch (err) {}
  }, [navigate])

  // Removed local useRefs for boot and launch URL state to prevent memory resets on remount

  useEffect(() => {
    initialize()
    listenForDeepLink()

    if (Capacitor.isNativePlatform()) {
      // Track initial boot
      trackEvent('app_opened', { platform: 'native' })

      const navListener = CapacitorApp.addListener('appUrlOpen', ({ url }: { url: string }) => {
        handleDeepLink(url)
      })

      // The "Wake Up Override"
      // Whenever the OS unfreezes the app from the background, force a silent global sync.
      // This ensures collaborative changes are instantly visible without a manual pull-to-refresh.
      const stateListener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          const isColdBoot = !globalHasBooted;
          globalHasBooted = true;

          if (usePickIntentStore.getState().isSavingPick) {
            console.log('[SYNC] Mutation in progress. Skipping wake-up sync to preserve optimistic UI.')
            return
          }
          
          console.log(`[SYNC] App woke up from sleep (Cold Boot: ${isColdBoot}). Forcing background sync...`)
          
          // 1. Immediate sync for Warm Boots
          // (On Cold Boots, React Query fetches instantly on mount, but this ensures coverage)
          queryClient.invalidateQueries({ queryKey: ['picks'] })
          queryClient.invalidateQueries({ queryKey: ['collections'] })
          queryClient.invalidateQueries({ queryKey: ['folders'] })
          queryClient.invalidateQueries({ queryKey: ['library'] })

          // 2. The 1.5s Safety Net Sync
          // Swift native background uploads take ~1s to finish (slower if Edge Function cold starts).
          // We wait 1.5s to guarantee we fetch the newest pick after the splash screen vanishes.
          setTimeout(() => {
             console.log('[SYNC] Firing 1.5s delayed safety sync to catch native background uploads...')
             queryClient.invalidateQueries({ queryKey: ['picks'] })
             queryClient.invalidateQueries({ queryKey: ['collections'] })
             queryClient.invalidateQueries({ queryKey: ['folders'] })
             queryClient.invalidateQueries({ queryKey: ['library'] })
          }, 1500)
        }
      })

      // Backup Listener: If Capacitor misses the appStateChange event (e.g. rapid foreground/background 
      // switching with native Share Sheets), the web browser's visibility event will catch it.
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && globalHasBooted) {
          if (usePickIntentStore.getState().isSavingPick) {
            console.log('[SYNC] Mutation in progress. Skipping visibility sync to preserve optimistic UI.')
            return
          }
          console.log('[SYNC] Document became visible. Forcing fallback sync...')
          queryClient.invalidateQueries({ queryKey: ['picks'] })
          queryClient.refetchQueries({ queryKey: ['picks'] })
        }
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)

      // Only process the launch URL once per app lifecycle
      if (!globalProcessedLaunchUrl) {
        CapacitorApp.getLaunchUrl().then((launchUrl) => {
          if (launchUrl?.url) {
            globalProcessedLaunchUrl = true
            setTimeout(() => handleDeepLink(launchUrl.url), 100)
          }
        })
      }
      
      // Global Network Listener
      const offlineToastId = 'global-offline-toast'
      const handleOffline = () => {
        toast('Weak or no connection', {
          id: offlineToastId,
          description: 'Shuvo may not sync properly.',
          icon: <WifiOff className="h-4 w-4 text-red-500" />,
          duration: 10000000,
          dismissible: true,
        })
      }
      
      const handleOnline = () => {
        toast.dismiss(offlineToastId)
      }
      
      window.addEventListener('offline', handleOffline)
      window.addEventListener('online', handleOnline)
      
      // Initial check
      if (!navigator.onLine) {
        handleOffline()
      }

      return () => { 
        navListener.then(l => l.remove()) 
        stateListener.then(l => l.remove())
        window.removeEventListener('offline', handleOffline)
        window.removeEventListener('online', handleOnline)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    } else {
      // Track initial boot for web
      trackEvent('app_opened', { platform: 'web' })

      // Web fallback network listener
      const offlineToastId = 'global-offline-toast'
      const handleOffline = () => {
        toast('Weak or no connection', {
          id: offlineToastId,
          description: 'Shuvo may not sync properly.',
          icon: <WifiOff className="h-4 w-4 text-red-500" />,
          duration: 10000000,
          dismissible: true,
        })
      }
      
      const handleOnline = () => {
        toast.dismiss(offlineToastId)
      }
      
      window.addEventListener('offline', handleOffline)
      window.addEventListener('online', handleOnline)
      
      if (!navigator.onLine) {
        handleOffline()
      }

      return () => {
        window.removeEventListener('offline', handleOffline)
        window.removeEventListener('online', handleOnline)
      }
    }
  }, [initialize, listenForDeepLink, handleDeepLink])

  return null
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <ErrorBoundary>
          <ForceUpdateGate>
            <AppManagers />
            <AppMainContent />
          </ForceUpdateGate>
        </ErrorBoundary>
      </HashRouter>
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  )
}

function AppMainContent() {
  const location = useLocation()
  const navigationType = useNavigationType()
  const { initialized } = useAuthStore()

  // 1. UI Setup (Status Bar & Splash Removal)
  useEffect(() => {
    if (initialized) {
      const initialStyle = document.getElementById('initial-splash-style')
      if (initialStyle) initialStyle.remove()
    }
    
    const setupUI = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await StatusBar.setOverlaysWebView({ overlay: true })
        } catch (err) {}
      }
    }
    setupUI()
  }, [initialized])

  const isWeb = Capacitor.getPlatform() === 'web'
  
  // HashRouter Fix: Check both the router's internal path and the actual browser path.
  // This handles links like shuvo.pro/invite/token which start without a #
  const browserPath = window.location.pathname
  const isPublicRoute = location.pathname.startsWith('/s/') || 
                       location.pathname.startsWith('/auth') || 
                       location.pathname.startsWith('/preview') ||
                       location.pathname.startsWith('/invite') ||
                       location.pathname.startsWith('/upgrade') ||
                       location.pathname.startsWith('/legal') ||
                       location.pathname.startsWith('/admin') ||
                       (isWeb && (browserPath.startsWith('/s/') || browserPath.startsWith('/invite/') || browserPath.startsWith('/legal/') || browserPath.startsWith('/admin')))

  useEffect(() => {
    if (isWeb && (browserPath.startsWith('/s/') || browserPath.startsWith('/invite/') || browserPath.startsWith('/legal/') || browserPath.startsWith('/admin'))) {
      // Redirect to the hash version so HashRouter can take over
      window.location.replace(`${window.location.origin}/#${browserPath}${window.location.search}`)
    }
  }, [isWeb, browserPath])

  // Overlay logic (Slide-up drawer simulation)
  const isOverlay = location.pathname.startsWith('/upgrade') || location.pathname.startsWith('/preview/')
  const lastNonOverlayLocation = useRef(location)
  if (!isOverlay) lastNonOverlayLocation.current = location

  // Simple Action State
  const action = useMemo(() => {
    return navigationType === 'REPLACE' ? 'PUSH' : navigationType
  }, [navigationType])

  // Track the most recent preview token to keep it mounted under the upgrade page
  const [activePreviewToken, setActivePreviewToken] = useState<string | null>(null)
  
  useEffect(() => {
    if (location.pathname.startsWith('/preview/')) {
      const token = location.pathname.split('/')[2]
      if (token) setActivePreviewToken(token)
    } else if (!isOverlay) {
      // Clear token if we go back to a non-overlay route (like Home)
      setActivePreviewToken(null)
    }
  }, [location.pathname, isOverlay])

  return (
    <NavigationContext.Provider value={{ action }}>
      <div className="relative h-full w-full overflow-hidden bg-background">
        <Suspense fallback={<div className="flex h-screen w-screen items-center justify-center bg-[#34D399]"></div>}>
          <Routes location={lastNonOverlayLocation.current}>
            {isWeb && !isPublicRoute ? (
              <Route path="*" element={<LandingPage />} />
            ) : (
              <>
                <Route path="/s/:shareToken" element={<PublicCollectionPreview />} />
                <Route path="/invite/:shareToken" element={<PublicCollectionPreview />} />
                <Route path="/legal/:doc" element={<LegalPage />} />
                <Route path="/debug/ui" element={<DebugUI />} />
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route element={<ProtectedRoute><OnboardingGate><Layout /></OnboardingGate></ProtectedRoute>}>
                  <Route path="/" element={<Home />} />
                  <Route path="/collections" element={<Collections />} />
                  <Route path="/collection/:id" element={<CollectionDetail />} />
                  <Route path="/folder/:id" element={<FolderDetail />} />
                  <Route path="/unsorted" element={<Unsorted />} />
                  <Route path="/favourites" element={<Favourites />} />
                  <Route path="/details" element={<Details />} />
                  <Route path="/pick/:id" element={<PickDetail />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="/legal" element={<LegalSettings />} />
                </Route>
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/auth/method" element={<AuthMethod />} />
                <Route path="/auth/email" element={<EmailAuth />} />
                <Route path="/auth/verify" element={<VerifyOtp />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                {!isWeb && <Route path="/invite/:token" element={<InviteAccept />} />}
                {/* 
                   Note: We keep these routes here for React Router's internal matching, 
                   but the actual visual rendering is handled by the Stacked Overlays section below.
                */}
                <Route path="/preview/:token" element={<div />} />
                <Route path="/upgrade" element={<div />} />
              </>
            )}
          </Routes>
        </Suspense>

        {/* Stacked Overlays Layer */}
        <Suspense fallback={null}>
          {(location.pathname.startsWith('/preview/') || (location.pathname.startsWith('/upgrade') && activePreviewToken)) && (
            <div className="fixed inset-0 z-[100]">
              <CollectionPreview tokenOverride={activePreviewToken || undefined} />
            </div>
          )}
          
          {location.pathname.startsWith('/upgrade') && (
            <div className="fixed inset-0 z-[110]">
              <ProUpgrade />
            </div>
          )}
        </Suspense>
        
        {!Capacitor.isNativePlatform() && <ReloadPrompt />}
        <Toaster />
      </div>
    </NavigationContext.Provider>
  )
}

export default App
